import * as THREE from 'three';
import { axleHex, corniceLayout, gearAt, gearLayout, hexToWorld, workshopLayout, type BattleState, type CorniceLayout, type GearLayout, type GolemUnit, type MapDef, type WorkshopLayout } from '@somnhold/sim';
import { Kit, place } from '../seasons/kit.ts';
import type { OmenCtx } from '../omens/common.ts';
import { gear } from '../models/clockwork.ts';
import { metal, std } from '../materials.ts';
import { WORLD_PER_HEX } from './coords.ts';
import { GlowLayer } from './glow.ts';
import { SkyDome, smoothTiles } from './snow.ts';
import type { TerrainBuild } from './terrain.ts';

/**
 * Nadd's workshop on the Flank Cliff (docs/sendas/artifice.md, chapter 1), for the artificer's path: slate and iron,
 * the sea far below, the cold of the rock; the only warm things the golems' red lamps, the boilers and the forges.
 *
 * - `cornice` (act 1): the goat path up the cliff. The wall on the west in tall slate columns, darker as they rise; the
 *   nests cut into it a step up (twigs and bones), the rests (a bench of stone, a cairn); the drop on the east cut out
 *   of the board, the cliff's face going down to the sea, its swell and foam far below, gulls; at the top the
 *   workshop's door cut in the rock, its lantern. A stone coming down shows as its shadow on the ledge (a dark disc in
 *   a red ring that closes as it nears) and dust; Grillo's lamp points at a slinger's nest (a red beam).
 * - `workshop` (act 2): the five halls in the rock, worn flags, cold forges, chains hanging, pulleys on the walls, the
 *   golems' posts (a plinth, a niche); the grilles over the stairs (drop and rise); the lift's landing at the end.
 * - `gearHall` (act 3, the test): the lift's hall, round; the gear bed in its floor (an iron plate, the axles' hubs),
 *   the gears where they lie, sit or are carried, turning their way at their speed with a red arrow over each; the water
 *   wheel in the west wall turned by the falling water, its belt to the wheel's axle; the drum in the east and its chain
 *   up to the lift's cage over the shaft, the cage going down once the belt takes the train.
 * - `bastionGate` (the opening's cinematic): the bastion's road at night up to its gate, torches on the towers.
 * - `forgeYard` (the opening): the bastion's yard at night by the forge (the forge itself is the scene's structure,
 *   where it is built), an anvil and a brazier.
 * - `shopTop`, `naddForge`, `tormoHall` (the scenes): the workshop's top room with its shaft and chains, Nadd's forge
 *   with the soup (the campfire is the scene's), Tormo's cavern with its one lamp.
 *
 * The golems (the fight's): their lamps' cones on the floor (warm while they sleep, red awake, none out), the arc their
 * arm will strike while it is up (red, pulsing), their open boilers' glow and steam.
 *
 * Draw calls: the dressing (one merged mesh), the sea and its foam (cornice), the grilles, the gears (one mesh each,
 * five at most), their arrows, the cones and arcs (instanced pools), the wheel and the cage, the glow layer, the sky.
 * No lights of its own: the board's are repainted (HOURS).
 */

export const TALLER_PLACES = new Set(['cornice', 'workshop', 'gearHall', 'bastionGate', 'forgeYard', 'shopTop', 'naddForge', 'tormoHall']);

const S = WORLD_PER_HEX;
const K = S / 1000;
const RED = new THREE.Color('#ff4f3a');
const RED_SOFT = new THREE.Color('#ff8a6e');
const WARM = new THREE.Color('#ffb48a');
const COLD = new THREE.Color('#dce8ff');
const SLATE = ['#3c434d', '#343a43', '#47505b', '#2c3139', '#262a31'];
const FLAG = ['#4c525b', '#555c65', '#454b54'];
const IRON = '#2c3036';
const WOOD = ['#3a3430', '#4a4038', '#2e2925'];

type Hour = 'day' | 'dusk' | 'night' | 'dawn';

/** The light by the hour: outdoors on the cliff (cold, the sea's blue in it), and inside the rock (`inside`: dark). */
const HOURS: Record<Hour | 'inside', { bg: string; fog: [string, number, number]; hemi: [string, string, number]; key: [string, number]; rim: [string, number]; sky: [string, string, string] }> = {
  day: { bg: '#9fb0c2', fog: ['#9fb0c2', 30, 120], hemi: ['#dbe4f0', '#2a2d34', 2.2], key: ['#fff6ee', 2.8], rim: ['#a9bfee', 1.4], sky: ['#5f7896', '#c9d5e2', '#ffffff'] },
  dawn: { bg: '#a9b5c4', fog: ['#aab6c4', 30, 120], hemi: ['#d0d9e6', '#25272e', 2.0], key: ['#ffece2', 2.8], rim: ['#a9bfee', 1.5], sky: ['#5a6d88', '#d7dee8', '#fff1ea'] },
  dusk: { bg: '#3d475b', fog: ['#46506a', 28, 120], hemi: ['#c3cfe2', '#1c1f27', 2.6], key: ['#ffe2d4', 3.0], rim: ['#9ab4f0', 2.0], sky: ['#18203a', '#6a7694', '#ff9a7a'] },
  night: { bg: '#070a10', fog: ['#0b0f18', 22, 90], hemi: ['#7487b0', '#0b0c10', 1.15], key: ['#a9bbe6', 0.95], rim: ['#7a96ff', 1.2], sky: ['#020409', '#0f1520', '#9fb2e2'] },
  inside: { bg: '#050608', fog: ['#07080b', 18, 52], hemi: ['#a3afc6', '#1a1412', 2.15], key: ['#d6deee', 1.7], rim: ['#ff7a5a', 0.75], sky: ['#000000', '#000000', '#000000'] },
};

/** The sea far below the cliff: a slow swell, its foam in lines, darker toward the horizon. */
const seaMat = (): THREE.ShaderMaterial =>
  new THREE.ShaderMaterial({
    uniforms: { time: { value: 0 }, dusk: { value: 1 } },
    vertexShader: /* glsl */ `
      varying vec2 vXZ;
      void main() { vec4 w = modelMatrix * vec4(position, 1.0); vXZ = w.xz; gl_Position = projectionMatrix * viewMatrix * w; }`,
    fragmentShader: /* glsl */ `
      uniform float time; uniform float dusk; varying vec2 vXZ;
      float h(vec2 p) { p = mod(p, 289.0); return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
      float n(vec2 p) { vec2 i = floor(p), f = fract(p); vec2 u = f * f * (3.0 - 2.0 * f);
        return mix(mix(h(i), h(i + vec2(1, 0)), u.x), mix(h(i + vec2(0, 1)), h(i + vec2(1, 1)), u.x), u.y); }
      void main() {
        float sw = n(vXZ * 0.16 + vec2(time * 0.05, time * 0.02)) * 0.6 + n(vXZ * 0.42 - vec2(time * 0.08, 0.0)) * 0.4;
        // Thin lines of foam on the swell (small and broken: from this high they are streaks, not clouds).
        float foam = smoothstep(0.74, 0.82, n(vec2(vXZ.x * 0.5 - time * 0.12, vXZ.y * 1.6 + sw * 3.0))) * smoothstep(0.35, 0.6, sw);
        // Toward the cliff (west) the swell breaks white against the rock.
        float shore = smoothstep(14.0, 0.0, vXZ.x - 6.0);
        vec3 deep = mix(vec3(0.09, 0.15, 0.22), vec3(0.04, 0.07, 0.11), dusk * 0.5);
        vec3 col = mix(deep, vec3(0.16, 0.24, 0.32), sw * 0.6);
        col = mix(col, vec3(0.8, 0.86, 0.92), max(foam * 0.45, shore * smoothstep(0.45, 0.7, sw) * 0.7));
        gl_FragColor = vec4(col, 1.0);
      }`,
  });

/** A floor fan (a cone's footprint) of unit radius along +x, `seg` slices over `half` radians either side. */
const fanGeo = (half: number, seg = 14, rim = 0.2): THREE.BufferGeometry => {
  // Two rings: bright near its source, fading out to `rim` at its end (a light thrown on the floor, not a decal).
  const pos: number[] = [0, 0, 0];
  const col: number[] = [1, 1, 1];
  for (const [d, c] of [[0.5, 0.75], [1, rim]] as const)
    for (let i = 0; i <= seg; i++) {
      const a = -half + (2 * half * i) / seg;
      pos.push(Math.cos(a) * d, 0, Math.sin(a) * d);
      col.push(c, c, c);
    }
  const idx: number[] = [];
  const n = seg + 1;
  for (let i = 0; i < seg; i++) {
    idx.push(0, 1 + i + 1, 1 + i);
    const a = 1 + i;
    const b = 1 + n + i;
    idx.push(a, a + 1, b + 1, a, b + 1, b);
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  g.setAttribute('color', new THREE.Float32BufferAttribute(col, 3));
  g.setIndex(idx);
  return g;
};

/** A gear's radius on the board by its teeth (world units): they mesh across two hexes. */
const gearR = (teeth: number): number => S * (0.42 + (teeth / 36) * 0.7);

/**
 * A gear as one solid (one draw call): its rim cut in square teeth, a hub, four lightening holes, `depth` thick with
 * a bevel that catches the light. Lies flat, its thickness up, centred on its axle.
 */
const gearGeo = (r: number, teeth: number, depth: number): THREE.BufferGeometry => {
  const shape = new THREE.Shape();
  const inner = r * 0.84;
  for (let i = 0; i < teeth; i++) {
    const a0 = (i / teeth) * Math.PI * 2;
    const step = (Math.PI * 2) / teeth;
    const pts: [number, number][] = [
      [inner, a0],
      [r, a0 + step * 0.12],
      [r, a0 + step * 0.48],
      [inner, a0 + step * 0.6],
    ];
    pts.forEach(([d, a], k) => (i === 0 && k === 0 ? shape.moveTo(Math.cos(a) * d, Math.sin(a) * d) : shape.lineTo(Math.cos(a) * d, Math.sin(a) * d)));
  }
  shape.closePath();
  const hub = new THREE.Path();
  hub.absarc(0, 0, r * 0.13, 0, Math.PI * 2, true);
  shape.holes.push(hub);
  for (let k = 0; k < 4; k++) {
    const a = (k / 4) * Math.PI * 2 + Math.PI / 4;
    const hole = new THREE.Path();
    hole.absarc(Math.cos(a) * r * 0.5, Math.sin(a) * r * 0.5, r * 0.17, 0, Math.PI * 2, true);
    shape.holes.push(hole);
  }
  const g = new THREE.ExtrudeGeometry(shape, { depth, bevelEnabled: true, bevelThickness: depth * 0.25, bevelSize: r * 0.03, bevelSegments: 1, curveSegments: 6 });
  g.rotateX(-Math.PI / 2);
  return g;
};

interface GearView {
  g: THREE.Group;
  spin: THREE.Group;
  teeth: number;
  angle: number;
  pos: THREE.Vector3;
  tilt: number;
}

export class TallerLayer {
  readonly group = new THREE.Group();
  private mats: THREE.Material[] = [];
  private geos: THREE.BufferGeometry[] = [];
  private glow = new GlowLayer(160);
  private dome: SkyDome | null = null;
  private terrain: TerrainBuild | null = null;
  private now = 0;
  private tickNow = 0;
  private inside: boolean;
  /** Static glows (lamps, embers, torches): where, what color, how big, how bright, flicker seed. */
  private lights: { p: THREE.Vector3; c: THREE.Color; size: number; a: number; seed: number; flick: number }[] = [];
  private broken = false;
  // The goat path.
  private C: CorniceLayout | null = null;
  private sea: THREE.ShaderMaterial | null = null;
  private shadows: THREE.InstancedMesh | null = null;
  private pointer: { from: number; nest: number; until: number } | null = null;
  private gulls: { m: THREE.Mesh; r: number; y: number; ph: number; c: THREE.Vector3 }[] = [];
  private dropX = new Map<number, number>();
  // The workshop.
  private W: WorkshopLayout | null = null;
  private grilles: { mesh: THREE.Group; y: number; want: number }[] = [];
  // The lift's hall.
  private Gl: GearLayout | null = null;
  private gears: GearView[] = [];
  private arrows: THREE.Mesh[] = [];
  private wheel: THREE.Group | null = null;
  private drum: THREE.Group | null = null;
  private cage: THREE.Group | null = null;
  private cageY = 0;
  private beltOn = false;
  private axleAt: THREE.Vector3[] = [];
  private axleLevel: number[] = [];
  private jamFlash = 0;
  private rightFlash = 0;
  private sockets: THREE.Mesh[] = [];
  private sockMat: THREE.MeshBasicMaterial | null = null;
  private askMat: THREE.MeshBasicMaterial | null = null;
  // The golems.
  private cones: THREE.InstancedMesh | null = null;
  private arcs: THREE.InstancedMesh | null = null;
  private golemGlow: { p: THREE.Vector3; c: THREE.Color; size: number; a: number }[] = [];
  private m4 = new THREE.Matrix4();
  private q4 = new THREE.Quaternion();
  private v3 = new THREE.Vector3();
  private s3 = new THREE.Vector3();
  private c3 = new THREE.Color();

  constructor(
    private ctx: OmenCtx,
    private map: MapDef,
    readonly place: string,
    s: BattleState,
  ) {
    this.group.name = `taller-${place}`;
    this.inside = place !== 'cornice' && place !== 'bastionGate' && place !== 'forgeYard';
    const kit = new Kit();
    if (place === 'cornice') this.buildCornice(kit, s);
    else if (place === 'workshop') this.buildWorkshop(kit, s);
    else if (place === 'gearHall') this.buildGearHall(kit, s);
    else if (place === 'bastionGate') this.buildGate(kit);
    else if (place === 'forgeYard') this.buildYard(kit);
    else this.buildRoom(kit, place);
    if (s.rules?.senda?.golem) this.buildGolemMarks();
    const mat = new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.85, metalness: 0.06, flatShading: true });
    this.mats.push(mat);
    const dress = kit.build(mat, 'taller-dress');
    if (dress) {
      dress.castShadow = true;
      dress.receiveShadow = true;
      this.geos.push(dress.geometry);
      this.group.add(dress);
    }
    this.group.add(this.glow.mesh);
  }

  /* ---------- helpers ---------- */

  private track<T extends THREE.Material | THREE.BufferGeometry>(x: T): T {
    if (x instanceof THREE.Material) this.mats.push(x);
    else this.geos.push(x);
    return x;
  }

  private hex(q: number, r: number, h = 0): THREE.Vector3 {
    const c = hexToWorld(q, r);
    return this.ctx.at(c.x, c.y, h);
  }

  private light(p: THREE.Vector3, color: THREE.ColorRepresentation, size: number, a: number, flick = 0): void {
    this.lights.push({ p: p.clone(), c: new THREE.Color(color), size, a, seed: this.lights.length * 1.37, flick });
  }

  /** A hanging lantern: its iron cage and a cold white light (no gold in it). */
  private lantern(kit: Kit, p: THREE.Vector3, hang = 0.9, color: THREE.ColorRepresentation = COLD): void {
    const box = new THREE.BoxGeometry(1, 1, 1);
    kit.add(box, IRON, place(p.x, p.y + hang + 0.45, p.z, 0, new THREE.Vector3(0.025, 0.9, 0.025)));
    kit.add(box, '#1c1f24', place(p.x, p.y + hang, p.z, Math.PI / 4, new THREE.Vector3(0.15, 0.2, 0.15)));
    kit.add(box, '#e8eef8', place(p.x, p.y + hang, p.z, Math.PI / 4, new THREE.Vector3(0.11, 0.14, 0.11)));
    box.dispose();
    this.light(new THREE.Vector3(p.x, p.y + hang, p.z), color, 1.9, 0.8, 1);
  }

  /** A wall of the rock round open cells: a block of dark slate on each rock cell next to one, tall and uneven. */
  private rockWalls(kit: Kit, rocks: { q: number; r: number }[], open: Set<string>, lo: number, hi: number, palette = SLATE): void {
    const prism = new THREE.CylinderGeometry(0.5, 0.58, 1, 6);
    const cap = new THREE.DodecahedronGeometry(0.5, 0);
    const near = (q: number, r: number): boolean => [[1, 0], [1, -1], [0, -1], [-1, 0], [-1, 1], [0, 1]].some(([dq, dr]) => open.has(`${q + (dq as number)},${r + (dr as number)}`));
    for (const h of rocks) {
      if (!near(h.q, h.r)) continue;
      const p = this.hex(h.q, h.r);
      const k = Math.sin(h.q * 12.9 + h.r * 78.2) * 0.5 + 0.5;
      const k2 = Math.sin(h.q * 3.1 + h.r * 7.7) * 0.5 + 0.5;
      const tall = lo + (hi - lo) * k;
      kit.add(prism, palette[Math.floor(k2 * (palette.length - 0.01))] as string, place(p.x, p.y + tall * 0.5, p.z, k * 5, new THREE.Vector3(1.08 + k2 * 0.2, tall, 1.04)));
      kit.add(cap, palette[(Math.floor(k * 3.99) + 1) % palette.length] as string, place(p.x + (k - 0.5) * 0.3, p.y + tall + 0.05, p.z, k * 7, new THREE.Vector3(1.0, 0.36, 0.95)));
    }
    prism.dispose();
    cap.dispose();
  }

  /**
   * The workshop's own walls: the rock dressed by the clan's masons. On each rock cell next to the floor a block of
   * coursed stone (two courses, offset, a coping on top), and every few an oak post with a beam over it; the face toward
   * the room a lighter stone so the walls read as walls, not as a cave.
   */
  /**
   * `lit` (the lift's hall): paler stone, a lamp on every third face, and the walls on the camera's side low (seen
   * from behind, full height, they were a black band along the bottom of the screen).
   */
  private workedWalls(kit: Kit, rocks: { q: number; r: number }[], open: Set<string>, tall = 2.3, lit = false): void {
    const box = new THREE.BoxGeometry(1, 1, 1);
    const dirs = [[1, 0], [1, -1], [0, -1], [-1, 0], [-1, 1], [0, 1]] as const;
    const STONE = lit ? ['#646a73', '#5c626b', '#6c727b'] : ['#4a5058', '#444a52', '#50565f'];
    let n = 0;
    for (const h of rocks) {
      const by = dirs.filter(([dq, dr]) => open.has(`${h.q + dq},${h.r + dr}`));
      if (!by.length) continue;
      const p = this.hex(h.q, h.r);
      const k = Math.sin(h.q * 12.9 + h.r * 78.2) * 0.5 + 0.5;
      // Which way it faces the room (the mean of its open neighbours).
      let fx = 0;
      let fz = 0;
      for (const [dq, dr] of by) {
        const c = hexToWorld(dq, dr);
        fx += c.x;
        fz += c.y;
      }
      const yaw = Math.atan2(fx, fz);
      const near = lit && fz < -0.3 * Math.hypot(fx, fz);
      const t = (near ? 0.9 : tall) + k * 0.35;
      kit.add(box, lit ? '#3a3f46' : '#2b2f35', place(p.x, p.y + t * 0.5, p.z, yaw, new THREE.Vector3(1.18 * S, t, 1.18 * S)));
      if (lit && !near && n++ % 3 === 0) {
        // A lamp on its iron arm, warm on the stone round it.
        const lx = p.x + Math.sin(yaw) * 0.68 * S;
        const lz = p.z + Math.cos(yaw) * 0.68 * S;
        kit.add(box, '#23272c', place(lx, p.y + t * 0.72, lz, yaw, new THREE.Vector3(0.06, 0.06, 0.34)));
        kit.add(box, '#3a3f46', place(lx + Math.sin(yaw) * 0.16, p.y + t * 0.62, lz + Math.cos(yaw) * 0.16, yaw, new THREE.Vector3(0.18, 0.22, 0.18)));
        this.light(new THREE.Vector3(lx + Math.sin(yaw) * 0.16, p.y + t * 0.62, lz + Math.cos(yaw) * 0.16), WARM, 2.6, 0.5, 0.5);
      }
      kit.add(box, STONE[Math.floor(k * 2.99)] as string, place(p.x, p.y + t * 0.27, p.z, yaw, new THREE.Vector3(1.2 * S, t * 0.5, 1.2 * S)));
      kit.add(box, STONE[(Math.floor(k * 2.99) + 1) % 3] as string, place(p.x, p.y + t * 0.74, p.z, yaw + 0.04, new THREE.Vector3(1.16 * S, t * 0.44, 1.16 * S)));
      kit.add(box, '#30353c', place(p.x, p.y + t + 0.06, p.z, yaw, new THREE.Vector3(1.24 * S, 0.12, 1.24 * S)));
      // An oak post now and then on its face, with a beam over it.
      if ((h.q * 7 + h.r * 3) % 4 === 0) {
        const px = p.x + Math.sin(yaw) * 0.62 * S;
        const pz = p.z + Math.cos(yaw) * 0.62 * S;
        kit.add(box, WOOD[0] as string, place(px, p.y + t * 0.5, pz, yaw, new THREE.Vector3(0.22, t, 0.22)));
        kit.add(box, WOOD[1] as string, place(px, p.y + t - 0.1, pz, yaw, new THREE.Vector3(1.3 * S, 0.2, 0.26)));
      }
    }
    box.dispose();
  }

  /** A run of pipe along a wall: iron, its joints and a valve wheel; a vent that puffs steam (in the frame). */
  private pipe(kit: Kit, a: THREE.Vector3, b: THREE.Vector3, y: number): void {
    const cyl = new THREE.CylinderGeometry(0.1, 0.1, 1, 8);
    const len = a.distanceTo(b);
    const mid = a.clone().add(b).multiplyScalar(0.5);
    const yaw = Math.atan2(b.x - a.x, b.z - a.z);
    kit.add(cyl, '#4a4036', place(mid.x, mid.y + y, mid.z, yaw, new THREE.Vector3(1, len, 1), Math.PI / 2, 0));
    const n = Math.max(2, Math.round(len / 1.6));
    for (let i = 0; i <= n; i++) {
      const p = a.clone().lerp(b, i / n);
      kit.add(cyl, '#2c3036', place(p.x, p.y + y, p.z, yaw, new THREE.Vector3(1.5, 0.12, 1.5), Math.PI / 2, 0));
      if (i === 1) {
        const w = new THREE.TorusGeometry(0.16, 0.03, 4, 10);
        kit.add(w, '#7a3028', place(p.x, p.y + y + 0.28, p.z, yaw + Math.PI / 2));
        w.dispose();
      }
      if (i % 2 === 1) this.vents.push(new THREE.Vector3(p.x, p.y + y + 0.1, p.z));
    }
    cyl.dispose();
  }

  /** Where steam puffs from the pipes (world points), and the next puff. */
  private vents: THREE.Vector3[] = [];
  private puff = 0;

  /* ---------- the goat path ---------- */

  private buildCornice(kit: Kit, s: BattleState): void {
    const L = corniceLayout(s.seed);
    this.C = L;
    const prism = new THREE.CylinderGeometry(0.45, 0.55, 1, 6);
    const cap = new THREE.DodecahedronGeometry(0.5, 0);
    const box = new THREE.BoxGeometry(1, 1, 1);
    const torus = new THREE.TorusGeometry(0.32, 0.12, 5, 10);
    const ledge = new Set([...L.ledge, ...L.nests, ...L.rests].map((h) => `${h.q},${h.r}`));
    // Each row's east edge of the ledge: where the drop begins.
    for (const h of L.ledge) {
      const x = hexToWorld(h.q, h.r).x;
      if (x > (this.dropX.get(h.r) ?? -1e9)) this.dropX.set(h.r, x);
    }
    // The wall: slate columns rising as they go west, ledges of lichen on them.
    for (const h of L.wall) {
      const p = this.hex(h.q, h.r);
      const k = Math.sin(h.q * 12.9 + h.r * 78.2) * 0.5 + 0.5;
      const by = [[1, 0], [1, -1], [0, 1], [0, -1]].some(([dq, dr]) => ledge.has(`${h.q + (dq as number)},${h.r + (dr as number)}`));
      const east = this.dropX.get(h.r) ?? 0;
      const away = Math.max(0, (east - hexToWorld(h.q, h.r).x) / 1000 - 2);
      // Low by the ledge (the camera looks over them at the path), rising as the cliff goes back.
      const tall = by ? 0.8 + k * 0.6 : 1.5 + Math.min(2.6, away * 0.45) + k * 0.8;
      kit.add(prism, SLATE[Math.floor(k * 4.99)] as string, place(p.x, p.y + tall * 0.5, p.z, k * 5, new THREE.Vector3(1.1 + k * 0.25, tall, 1.05)));
      kit.add(cap, k > 0.72 ? '#59625e' : SLATE[2] as string, place(p.x + 0.05, p.y + tall + 0.04, p.z, k * 7, new THREE.Vector3(0.9, 0.24, 0.85)));
    }
    // The nests: a step up into the wall, a ring of twigs on it, bones and feathers.
    for (const h of L.nests) {
      const p = this.hex(h.q, h.r);
      kit.add(box, SLATE[3] as string, place(p.x - 0.05, p.y + 0.14, p.z, 0.2, new THREE.Vector3(0.92, 0.28, 0.86)));
      kit.add(torus, '#4b443c', place(p.x - 0.05, p.y + 0.36, p.z, 0.4, new THREE.Vector3(1, 1, 0.55), Math.PI / 2, 0));
      kit.add(torus, '#3b352f', place(p.x - 0.1, p.y + 0.42, p.z + 0.05, 1.1, new THREE.Vector3(0.8, 0.8, 0.45), Math.PI / 2, 0));
      kit.add(box, '#c9c6bf', place(p.x + 0.1, p.y + 0.36, p.z - 0.1, 0.7, new THREE.Vector3(0.24, 0.03, 0.03)));
      kit.add(box, SLATE[4] as string, place(p.x - 0.3, p.y + 1.7, p.z, 0.1, new THREE.Vector3(1.2, 0.4, 1.1)));
    }
    // The rests: a bench of stone against the wall and a cairn of three stones.
    for (const h of L.rests) {
      const p = this.hex(h.q, h.r);
      kit.add(box, '#5d646e', place(p.x - 0.1, p.y + 0.18, p.z, 0.05, new THREE.Vector3(0.85, 0.32, 0.38)));
      for (let i = 0; i < 3; i++) kit.add(cap, SLATE[2 + (i % 2)] as string, place(p.x - 0.25, p.y + 0.38 + i * 0.16, p.z + 0.3, i, 0.28 - i * 0.05));
    }
    // The ledge's lip: loose stones along the east edge, the cliff's face going down from it, the sea far below.
    const rows = [...this.dropX.keys()].sort((a, b) => a - b);
    for (const r of rows) {
      const lip = ((this.dropX.get(r) ?? 0) + 600) * K;
      const z = r * 866 * K;
      const k = Math.sin(r * 3.3) * 0.5 + 0.5;
      const g = this.ctx.ground(lip - 0.3, z);
      kit.add(cap, SLATE[1] as string, place(lip - 0.1, g + 0.05, z, k * 4, new THREE.Vector3(0.36, 0.18, 0.6)));
      kit.add(box, SLATE[3 + (r & 1)] as string, place(lip + 0.25, g - 3.4, z, 0, new THREE.Vector3(0.6, 6.8, 0.866 * S + 0.1)));
      kit.add(box, SLATE[4] as string, place(lip + 0.9 + k * 0.6, g - 9.5, z + 0.2, 0.1, new THREE.Vector3(1.0, 7, 0.95 * S)));
    }
    // The workshop's door at the top: a frame cut in the rock, an iron-bound door, its lantern.
    const d = this.ctx.at(L.door.x, L.door.y + 300, 0);
    kit.add(box, SLATE[4] as string, place(d.x, d.y + 1.4, d.z - 0.35, 0, new THREE.Vector3(2.4, 2.8, 0.6)));
    kit.add(box, '#2e2925', place(d.x, d.y + 0.85, d.z - 0.02, 0, new THREE.Vector3(1.0, 1.7, 0.12)));
    for (const y of [0.35, 0.85, 1.35]) kit.add(box, IRON, place(d.x, d.y + y, d.z + 0.05, 0, new THREE.Vector3(1.04, 0.07, 0.04)));
    for (const k of [-1, 1]) kit.add(box, '#5a616b', place(d.x + k * 0.65, d.y + 0.95, d.z, 0, new THREE.Vector3(0.22, 1.9, 0.3)));
    kit.add(box, '#5a616b', place(d.x, d.y + 1.95, d.z, 0, new THREE.Vector3(1.6, 0.26, 0.32)));
    this.lantern(kit, new THREE.Vector3(d.x + 0.95, d.y + 1.0, d.z + 0.2), 0.3, WARM);
    // A pulley and a rope above the door (the workshop's hoist).
    const pul = gear(this.group, 0.3, 10, metal('#4a4e55', { roughness: 0.5 }), 0.06);
    this.track((pul.children[0] as THREE.Mesh)?.material as THREE.Material);
    pul.position.set(d.x - 0.9, d.y + 2.6, d.z + 0.1);
    // The sea far below and its foam; distant headlands in the haze.
    const sea = seaMat();
    this.sea = sea;
    this.track(sea);
    const plane = new THREE.Mesh(this.track(new THREE.PlaneGeometry(260, 260).rotateX(-Math.PI / 2)), sea);
    plane.position.set(120, -15, 0);
    plane.name = 'taller-sea';
    this.group.add(plane);
    for (let i = 0; i < 5; i++) {
      const z = (-30 + i * 16) * 1.0;
      kit.add(cap, SLATE[4] as string, place(60 + i * 9, -14, z, i, new THREE.Vector3(9 + i * 2, 6 + (i % 3) * 3, 7)));
    }
    // Gulls: a few pale chevrons wheeling over the drop.
    const gullGeo = this.track(new THREE.BufferGeometry());
    gullGeo.setAttribute('position', new THREE.Float32BufferAttribute([-0.35, 0.06, 0, 0, 0, 0.05, 0, 0, -0.05, 0.35, 0.06, 0, 0, 0, 0.05, 0, 0, -0.05], 3));
    const gullMat = this.track(new THREE.MeshBasicMaterial({ color: '#e6eaef', side: THREE.DoubleSide }));
    for (let i = 0; i < 7; i++) {
      const m = new THREE.Mesh(gullGeo, gullMat);
      const c = new THREE.Vector3(8 + (i % 3) * 4, 0, -10 + i * 3.4);
      this.gulls.push({ m, r: 2.5 + (i % 4), y: 1.5 + (i % 3) * 1.6, ph: i * 1.9, c });
      this.group.add(m);
    }
    // The stones' shadows on the ledge: a dark disc and a red ring each (instanced, eight at most).
    const disc = this.track(new THREE.CircleGeometry(1, 20).rotateX(-Math.PI / 2));
    const sm = this.track(new THREE.MeshBasicMaterial({ color: '#ffffff', transparent: true, opacity: 0.55, depthWrite: false, polygonOffset: true, polygonOffsetFactor: -4 }));
    const sh = new THREE.InstancedMesh(disc, sm, 16);
    sh.instanceColor = new THREE.InstancedBufferAttribute(new Float32Array(16 * 3), 3);
    sh.count = 0;
    sh.renderOrder = 4;
    sh.frustumCulled = false;
    sh.name = 'taller-stones';
    this.shadows = sh;
    this.group.add(sh);
    prism.dispose();
    cap.dispose();
    box.dispose();
    torus.dispose();
  }

  /** The drop east of the ledge (world point). */
  private voidAt = (x: number, z: number): boolean => {
    if (!this.dropX.size) return false;
    const row = Math.round(z / K / 866);
    const edge = this.dropX.get(Math.max(-11, Math.min(11, row)));
    if (edge === undefined) return x > 2 * S;
    return x / K > edge + 650;
  };

  /** The drop cut out of the board's ground (and the ground beyond, and what stands on it). */
  private carve(terrain: TerrainBuild): void {
    const cut = (mesh: THREE.Mesh): void => {
      const geo = mesh.geometry;
      const pos = geo.getAttribute('position') as THREE.BufferAttribute | undefined;
      if (!pos) return;
      mesh.updateMatrixWorld(true);
      const mw = mesh.matrixWorld;
      const v = new THREE.Vector3();
      const idx = geo.index;
      const tri = (i: number): number => (idx ? idx.getX(i) : i);
      const n = idx ? idx.count : pos.count;
      const keep: number[] = [];
      for (let i = 0; i < n; i += 3) {
        let cx = 0;
        let cz = 0;
        for (let k = 0; k < 3; k++) {
          v.fromBufferAttribute(pos, tri(i + k)).applyMatrix4(mw);
          cx += v.x;
          cz += v.z;
        }
        const out = this.voidAt(cx / 3, cz / 3);
        if (idx) {
          if (!out) keep.push(tri(i), tri(i + 1), tri(i + 2));
        } else if (out) {
          const x = pos.getX(i);
          const y = pos.getY(i);
          const z = pos.getZ(i);
          pos.setXYZ(i + 1, x, y, z);
          pos.setXYZ(i + 2, x, y, z);
        }
      }
      if (idx) geo.setIndex(keep);
      pos.needsUpdate = true;
      geo.computeBoundingSphere();
    };
    cut(terrain.mesh);
    const mm = new THREE.Matrix4();
    const p = new THREE.Vector3();
    const zero = new THREE.Matrix4().makeScale(0, 0, 0);
    terrain.decor.traverse((o) => {
      if (o.name === 'outskirts-ground' || o.name === 'roads') cut(o as THREE.Mesh);
      const im = o as THREE.InstancedMesh;
      if (!im.isInstancedMesh) return;
      let touched = false;
      for (let i = 0; i < im.count; i++) {
        im.getMatrixAt(i, mm);
        p.setFromMatrixPosition(mm);
        if (this.voidAt(p.x, p.z)) {
          im.setMatrixAt(i, zero);
          touched = true;
        }
      }
      if (touched) im.instanceMatrix.needsUpdate = true;
    });
  }

  /* ---------- the sleeping workshop ---------- */

  private buildWorkshop(kit: Kit, s: BattleState): void {
    const L = workshopLayout(s.seed);
    this.W = L;
    const open = new Set(L.floor.map((h) => `${h.q},${h.r}`));
    this.workedWalls(kit, L.rocks, open, 2.3);
    const box = new THREE.BoxGeometry(1, 1, 1);
    // Each hall its own: the sweepers' gallery, the forges, the foundry, the boilers, the lift's store.
    L.halls.forEach((hl, i) => {
      const top = (hl.cells.reduce((m, c) => Math.min(m, c.r), 99) as number) - 1;
      const xs = hl.cells.filter((c) => c.r === top + 1).map((c) => hexToWorld(c.q, c.r).x);
      const a = this.ctx.at(Math.min(...xs), (top + 0.75) * 866, 0);
      const b = this.ctx.at(Math.max(...xs), (top + 0.75) * 866, 0);
      this.pipe(kit, a, b, 1.7 + (i % 2) * 0.3);
      const m = this.ctx.at(hl.at.x, hl.at.y + 600, 0);
      if (i === 0) {
        // Brooms and a bench: the old sweepers' gallery.
        for (let k = 0; k < 4; k++) kit.add(box, WOOD[k % 3] as string, place(m.x - 2 + k * 0.3, m.y + 0.7, m.z + 1.3, 0, new THREE.Vector3(0.05, 1.4, 0.05), 0.25, 0));
        kit.add(box, WOOD[1] as string, place(m.x + 1.4, m.y + 0.35, m.z + 1.5, 0, new THREE.Vector3(1.8, 0.1, 0.5)));
      } else if (i === 2) {
        // The foundry: a crucible over coals, molten iron in it, ingot moulds by it.
        kit.add(new THREE.CylinderGeometry(0.42, 0.32, 0.6, 10), '#33373d', place(m.x + 2.4, m.y + 0.4, m.z + 1.2));
        kit.add(new THREE.CylinderGeometry(0.34, 0.34, 0.04, 10), '#ff6a40', place(m.x + 2.4, m.y + 0.72, m.z + 1.2));
        this.light(new THREE.Vector3(m.x + 2.4, m.y + 0.8, m.z + 1.2), RED_SOFT, 2.4, 0.85, 0.6);
        for (let k = 0; k < 4; k++) kit.add(box, '#2a2e34', place(m.x + 0.6 + k * 0.42, m.y + 0.08, m.z + 1.6, 0, new THREE.Vector3(0.34, 0.14, 0.2)));
      } else if (i === 3) {
        // The boilers: two great drums on their legs, riveted, their gauges.
        for (const dx of [-2.6, 2.6]) {
          kit.add(new THREE.CylinderGeometry(0.7, 0.7, 1.9, 12), '#4a4036', place(m.x + dx, m.y + 1.1, m.z + 1.2, 0, 1, 0, Math.PI / 2));
          kit.add(new THREE.CylinderGeometry(0.72, 0.72, 0.08, 12), '#2c3036', place(m.x + dx - 0.6, m.y + 1.1, m.z + 1.2, 0, 1, 0, Math.PI / 2));
          kit.add(new THREE.CylinderGeometry(0.72, 0.72, 0.08, 12), '#2c3036', place(m.x + dx + 0.6, m.y + 1.1, m.z + 1.2, 0, 1, 0, Math.PI / 2));
          this.light(new THREE.Vector3(m.x + dx, m.y + 0.5, m.z + 1.7), RED, 1.0, 0.5, 0.6);
          this.vents.push(new THREE.Vector3(m.x + dx, m.y + 1.9, m.z + 1.2));
        }
      } else if (i === 4) {
        // The lift's store: crates, coils of chain, a hand cart.
        for (let k = 0; k < 5; k++) kit.add(box, WOOD[k % 3] as string, place(m.x - 2.6 + (k % 3) * 0.7, m.y + 0.3 + Math.floor(k / 3) * 0.6, m.z + 1.3, k * 0.3, new THREE.Vector3(0.6, 0.6, 0.6)));
        kit.add(new THREE.TorusGeometry(0.3, 0.1, 5, 10), '#3a3f46', place(m.x + 1.2, m.y + 0.1, m.z + 1.4, 0, 1, Math.PI / 2, 0));
      }
    });
    const cyl = new THREE.CylinderGeometry(0.5, 0.5, 1, 10);
    const chainGeo = new THREE.TorusGeometry(0.05, 0.016, 4, 6);
    L.halls.forEach((hl, i) => {
      // The golems' posts: a plinth under each, its niche behind.
      for (const p of hl.posts) {
        const w = this.ctx.at(p.x, p.y, 0);
        kit.add(cyl, '#2a2e34', place(w.x, w.y + 0.05, w.z, 0, new THREE.Vector3(1.25, 0.1, 1.25)));
        kit.add(cyl, '#5a4e3e', place(w.x, w.y + 0.11, w.z, 0, new THREE.Vector3(1.1, 0.02, 1.1)));
        kit.add(box, '#15171b', place(w.x, w.y + 1.3, w.z - 0.9, 0, new THREE.Vector3(1.3, 2.4, 0.3)));
      }
      // The cold forges: a hearth of stone, its hood, an anvil before it, a few embers still in it.
      for (const f of hl.forges) {
        const p = this.hex(f.q, f.r);
        kit.add(box, '#4a4f57', place(p.x, p.y + 0.4, p.z + 0.3, 0, new THREE.Vector3(1.1, 0.8, 0.7)));
        kit.add(box, '#1a1c20', place(p.x, p.y + 0.62, p.z + 0.06, 0, new THREE.Vector3(0.7, 0.3, 0.2)));
        kit.add(box, '#33373d', place(p.x, p.y + 1.5, p.z + 0.4, 0, new THREE.Vector3(0.9, 0.9, 0.5), -0.2, 0));
        kit.add(box, IRON, place(p.x + 0.1, p.y + 0.3, p.z - 0.6, 0.3, new THREE.Vector3(0.5, 0.18, 0.22)));
        kit.add(box, IRON, place(p.x + 0.1, p.y + 0.14, p.z - 0.6, 0.3, new THREE.Vector3(0.22, 0.28, 0.16)));
        this.light(new THREE.Vector3(p.x, p.y + 0.62, p.z + 0.1), RED, 1.1, 0.35, 0.6);
      }
      // Chains down from the dark over each hall, and a pulley wheel on its wall.
      for (let k = 0; k < 3; k++) {
        const x = hl.at.x + (k - 1) * 2600;
        const w = this.ctx.at(x, hl.at.y + ((k % 2) - 0.5) * 900, 0);
        for (let j = 0; j < 9; j++) kit.add(chainGeo, '#3a3f46', place(w.x, w.y + 1.6 + j * 0.12, w.z, 0, 1, 0, j % 2 ? Math.PI / 2 : 0));
        kit.add(box, '#3a3f46', place(w.x, w.y + 1.5, w.z, 0, new THREE.Vector3(0.05, 0.14, 0.05)));
      }
      // A hanging lantern by each hall's way out (cold white: the halls read by them).
      this.lantern(kit, this.ctx.at(hl.out.x + (i % 2 ? 900 : -900), hl.out.y - 700, 0), 1.6);
    });
    // The lift's landing: a wooden platform in a frame, chains up into the dark.
    const lf = this.ctx.at(L.lift.x, L.lift.y, 0);
    kit.add(box, WOOD[1] as string, place(lf.x, lf.y + 0.06, lf.z, 0, new THREE.Vector3(1.5, 0.12, 1.5)));
    for (const [dx, dz] of [[-0.7, -0.7], [0.7, -0.7], [-0.7, 0.7], [0.7, 0.7]] as const) kit.add(box, WOOD[0] as string, place(lf.x + dx, lf.y + 1.2, lf.z + dz, 0, new THREE.Vector3(0.1, 2.4, 0.1)));
    kit.add(box, WOOD[2] as string, place(lf.x, lf.y + 2.4, lf.z, 0, new THREE.Vector3(1.6, 0.12, 1.6)));
    this.lantern(kit, new THREE.Vector3(lf.x, lf.y + 0.6, lf.z), 1.2, WARM);
    // The grilles: iron bars across each stair (drawn down when shut).
    const bar = this.track(new THREE.CylinderGeometry(0.035, 0.035, 2.2, 5));
    const barMat = this.track(metal('#3c4148', { roughness: 0.5 }));
    for (const hl of L.halls) {
      if (hl.stair.length < 2) continue;
      const a = this.hex(hl.stair[0]!.q, hl.stair[0]!.r);
      const b = this.hex(hl.stair[1]!.q, hl.stair[1]!.r);
      const g = new THREE.Group();
      const mid = a.clone().add(b).multiplyScalar(0.5);
      g.position.copy(mid);
      const span = a.distanceTo(b) + S * 0.9;
      const dir = new THREE.Vector3().subVectors(b, a).normalize();
      for (let k = 0; k < 9; k++) {
        const m = new THREE.Mesh(bar, barMat);
        m.position.copy(dir).multiplyScalar(-span / 2 + (k * span) / 8).setY(1.1);
        g.add(m);
      }
      const cross = new THREE.Mesh(this.track(new THREE.BoxGeometry(span, 0.08, 0.08)), barMat);
      cross.position.y = 1.6;
      cross.rotation.y = -Math.atan2(dir.z, dir.x);
      g.add(cross);
      g.name = 'taller-grille';
      this.group.add(g);
      this.grilles.push({ mesh: g, y: 2.6, want: 2.6 });
      g.position.y = mid.y + 2.6;
      g.visible = false;
      kit.add(box, '#15171b', place(mid.x, mid.y + 2.6, mid.z, -Math.atan2(dir.z, dir.x), new THREE.Vector3(span + 0.4, 0.3, 0.5)));
    }
    box.dispose();
    cyl.dispose();
    chainGeo.dispose();
  }

  /* ---------- the lift's hall: the gear train ---------- */

  private buildGearHall(kit: Kit, s: BattleState): void {
    const L = gearLayout(s.seed);
    this.Gl = L;
    const R = this.map.radius;
    const all: { q: number; r: number }[] = [];
    for (let r = -R; r <= R; r++) for (let q = -R; q <= R; q++) if (Math.max(Math.abs(q), Math.abs(r), Math.abs(q + r)) <= R) all.push({ q, r });
    const rocks = new Set(L.rocks.map((h) => `${h.q},${h.r}`));
    const open = new Set(all.filter((h) => !rocks.has(`${h.q},${h.r}`) && Math.max(Math.abs(h.q), Math.abs(h.r), Math.abs(h.q + h.r)) < L.radius).map((h) => `${h.q},${h.r}`));
    this.workedWalls(kit, L.rocks.filter((h) => Math.max(Math.abs(h.q), Math.abs(h.r), Math.abs(h.q + h.r)) >= L.radius), open, 2.6, true);
    const box = new THREE.BoxGeometry(1, 1, 1);
    const cyl = new THREE.CylinderGeometry(0.5, 0.5, 1, 14);
    // The gear bed: an iron plate set in the floor under the axles, a rim of stone round it.
    // The gear bed: iron plates riveted on the flags, a bronze rim round them, the seams between the plates.
    const bed = this.ctx.at(0, 0, 0);
    kit.add(cyl, '#6a5a44', place(bed.x, bed.y + 0.015, bed.z, 0, new THREE.Vector3(10.1 * S, 0.03, 8.3 * S)));
    kit.add(cyl, '#3f454d', place(bed.x, bed.y + 0.03, bed.z, 0, new THREE.Vector3(9.6 * S, 0.03, 7.8 * S)));
    for (let k = -3; k <= 3; k++) {
      kit.add(box, '#262a30', place(bed.x + k * 1.3 * S, bed.y + 0.05, bed.z, 0, new THREE.Vector3(0.05, 0.01, 7.0 * S * Math.sqrt(Math.max(0.05, 1 - (k / 3.7) ** 2)))));
    }
    for (let k = 0; k < 28; k++) {
      const a = (k / 28) * Math.PI * 2;
      kit.add(cyl, '#8a7458', place(bed.x + Math.cos(a) * 4.7 * S, bed.y + 0.05, bed.z + Math.sin(a) * 3.8 * S, 0, new THREE.Vector3(0.09, 0.03, 0.09)));
    }
    // The axles: a hub on each, its three heights by the lattice (neighbours never at the same one).
    L.axles.forEach((a, i) => {
      const h = axleHex(a);
      const p = this.hex(h.q, h.r);
      const level = ((((a.q - a.r) % 3) + 3) % 3) as 0 | 1 | 2;
      this.axleAt.push(p);
      this.axleLevel.push(level);
      // A bronze post on a flange, its collar: what a gear sits on (the wheel's and the drum's red-banded).
      kit.add(cyl, '#2a2e34', place(p.x, p.y + 0.05, p.z, 0, new THREE.Vector3(0.9, 0.08, 0.9)));
      kit.add(cyl, '#7a6a52', place(p.x, p.y + 0.12, p.z, 0, new THREE.Vector3(0.66, 0.08, 0.66)));
      kit.add(cyl, '#8a7a60', place(p.x, p.y + 0.36, p.z, 0, new THREE.Vector3(0.26, 0.5, 0.26)));
      kit.add(cyl, i < 2 ? '#8a2c22' : '#4c535c', place(p.x, p.y + 0.56, p.z, 0, new THREE.Vector3(0.36, 0.08, 0.36)));
    });
    // The water wheel in the west wall: the water falling down the rock, the wheel turning in its race.
    const wa = this.ctx.at(L.wheelAt.x, L.wheelAt.y, 0);
    kit.add(box, '#15181d', place(wa.x - 0.6, wa.y + 0.2, wa.z, 0, new THREE.Vector3(1.4, 0.4, 2.6)));
    const fall = new THREE.Mesh(this.track(new THREE.PlaneGeometry(0.6, 4.2)), this.track(new THREE.MeshBasicMaterial({ color: '#9fbcd4', transparent: true, opacity: 0.32, depthWrite: false, side: THREE.DoubleSide })));
    fall.position.set(wa.x - 0.95, wa.y + 2.1, wa.z);
    fall.rotation.y = Math.PI / 2;
    this.group.add(fall);
    const wheel = new THREE.Group();
    wheel.position.set(wa.x - 0.3, wa.y + 1.25, wa.z);
    const wood = this.track(std('#3e3630', { roughness: 0.9, flatShading: true }));
    const rim = new THREE.Mesh(this.track(new THREE.TorusGeometry(1.15, 0.08, 5, 20)), wood);
    rim.rotation.y = Math.PI / 2;
    wheel.add(rim);
    for (let k = 0; k < 10; k++) {
      const paddle = new THREE.Mesh(this.track(new THREE.BoxGeometry(0.5, 0.08, 0.36)), wood);
      const a = (k / 10) * Math.PI * 2;
      paddle.position.set(0, Math.sin(a) * 1.15, Math.cos(a) * 1.15);
      paddle.rotation.x = -a;
      wheel.add(paddle);
      const spoke = new THREE.Mesh(this.track(new THREE.BoxGeometry(0.06, 0.06, 1.1)), wood);
      spoke.position.set(0, Math.sin(a) * 0.57, Math.cos(a) * 0.57);
      spoke.rotation.x = -a;
      wheel.add(spoke);
    }
    this.group.add(wheel);
    this.wheel = wheel;
    // The belt from the wheel's hub down to the wheel's axle: two dark straps.
    const w0 = this.axleAt[0] as THREE.Vector3;
    for (const dz of [-0.16, 0.16]) {
      const a = new THREE.Vector3(wa.x - 0.3, wa.y + 1.25, wa.z + dz);
      const b = new THREE.Vector3(w0.x, w0.y + 0.4, w0.z + dz);
      const len = a.distanceTo(b);
      kit.add(box, '#6b2e24', place((a.x + b.x) / 2, (a.y + b.y) / 2, (a.z + b.z) / 2, 0, new THREE.Vector3(len, 0.06, 0.2), 0, Math.atan2(a.y - b.y, b.x - a.x)));
    }
    // The drum in the east: a drum in a frame, its chain up to the lift's pulley; the shaft, the cage over it.
    const dr = this.ctx.at(L.drumAt.x, L.drumAt.y, 0);
    for (const dz of [-0.7, 0.7]) kit.add(box, '#2a2e34', place(dr.x, dr.y + 0.6, dr.z + dz, 0, new THREE.Vector3(0.3, 1.2, 0.16)));
    const drum = new THREE.Group();
    drum.position.set(dr.x, dr.y + 0.9, dr.z);
    const dm = new THREE.Mesh(this.track(new THREE.CylinderGeometry(0.42, 0.42, 1.2, 14)), this.track(metal('#4a4036', { roughness: 0.6, flatShading: true })));
    dm.rotation.x = Math.PI / 2;
    drum.add(dm);
    for (let k = 0; k < 6; k++) {
      const slat = new THREE.Mesh(this.track(new THREE.BoxGeometry(0.06, 0.06, 1.24)), this.track(metal('#2a2e34', { roughness: 0.5 })));
      const a = (k / 6) * Math.PI * 2;
      slat.position.set(Math.cos(a) * 0.43, Math.sin(a) * 0.43, 0);
      drum.add(slat);
    }
    this.group.add(drum);
    this.drum = drum;
    const lift = this.ctx.at(L.lift.x, L.lift.y, 0);
    kit.add(cyl, '#050607', place(lift.x, lift.y + 0.02, lift.z, 0, new THREE.Vector3(1.7, 0.04, 1.7)));
    for (const [dx, dz] of [[-0.85, -0.85], [0.85, -0.85], [-0.85, 0.85], [0.85, 0.85]] as const) kit.add(box, WOOD[0] as string, place(lift.x + dx, lift.y + 1.6, lift.z + dz, 0, new THREE.Vector3(0.14, 3.2, 0.14)));
    kit.add(box, WOOD[2] as string, place(lift.x, lift.y + 3.2, lift.z, 0, new THREE.Vector3(2, 0.16, 2)));
    const cage = new THREE.Group();
    cage.position.set(lift.x, lift.y, lift.z);
    const cw = this.track(std('#4a4038', { roughness: 0.9, flatShading: true }));
    const floor = new THREE.Mesh(this.track(new THREE.BoxGeometry(1.4, 0.1, 1.4)), cw);
    floor.position.y = 0.06;
    cage.add(floor);
    for (const [dx, dz] of [[-0.66, -0.66], [0.66, -0.66], [-0.66, 0.66], [0.66, 0.66]] as const) {
      const post = new THREE.Mesh(this.track(new THREE.BoxGeometry(0.07, 1.3, 0.07)), cw);
      post.position.set(dx, 0.7, dz);
      cage.add(post);
    }
    const roof = new THREE.Mesh(this.track(new THREE.BoxGeometry(1.44, 0.08, 1.44)), cw);
    roof.position.y = 1.36;
    cage.add(roof);
    const chain = new THREE.Mesh(this.track(new THREE.CylinderGeometry(0.03, 0.03, 1.9, 4)), this.track(metal('#3a3f46', { roughness: 0.5 })));
    chain.position.y = 2.3;
    cage.add(chain);
    this.group.add(cage);
    this.cage = cage;
    kit.add(box, '#1a1614', place((dr.x + lift.x) / 2, 3.1, (dr.z + lift.z) / 2, 0, new THREE.Vector3(Math.hypot(dr.x - lift.x, dr.z - lift.z), 0.05, 0.05), 0, 0));
    // The golems' niches round the wall, the Overseer's by the lift.
    for (const p of [...L.posts, L.boss]) {
      const w = this.ctx.at(p.x, p.y, 0);
      kit.add(cyl, '#2a2e34', place(w.x, w.y + 0.05, w.z, 0, new THREE.Vector3(1.3, 0.1, 1.3)));
    }
    // Lanterns: by the door (Dunna's), by the wheel, over the lift; a few embers by the wall.
    this.lantern(kit, this.ctx.at(-4600, 3500, 0), 1.7);
    this.lantern(kit, this.ctx.at(L.wheelAt.x + 800, L.wheelAt.y - 1800, 0), 1.9);
    this.lantern(kit, this.ctx.at(L.lift.x - 900, L.lift.y - 900, 0), 1.8, WARM);
    this.lantern(kit, this.ctx.at(0, -5600, 0), 2.0);
    // The gears: one mesh each, where they lie.
    // The gears: one solid each (teeth, holes, a bevel), light steel for the small to darker for the large so the three
    // sizes read apart; a red hub. (Metalness low and a little emissive: with no sky to reflect, metal() at 0.8 drew
    // them black on a black floor, docs/RENDIMIENTO.md.)
    const steel = [12, 24, 36].map((t) => this.track(new THREE.MeshStandardMaterial({ color: t === 12 ? '#b9c2cc' : t === 24 ? '#98a2ad' : '#7f8893', roughness: 0.32, metalness: 0.45, flatShading: true, emissive: '#20262e' })));
    const hubMat = this.track(new THREE.MeshStandardMaterial({ color: '#8a2c22', roughness: 0.5, metalness: 0.3, emissive: '#2a0a06' }));
    const hubGeo = this.track(new THREE.CylinderGeometry(0.2, 0.2, 0.3, 10));
    L.pieces.forEach((t, k) => {
      const g = new THREE.Group();
      const spin = new THREE.Group();
      g.add(spin);
      const r = gearR(t);
      const body = new THREE.Mesh(this.track(gearGeo(r, Math.round(t / 2) + 4, 0.16)), steel[t >= 36 ? 2 : t >= 24 ? 1 : 0] as THREE.Material);
      body.castShadow = true;
      spin.add(body);
      const hub = new THREE.Mesh(hubGeo, hubMat);
      hub.position.y = 0.12;
      spin.add(hub);
      const p = this.lieAt(k);
      g.position.copy(p);
      this.group.add(g);
      this.gears.push({ g, spin, teeth: t, angle: k, pos: p.clone(), tilt: 0.05 });
    });
    // An empty axle shows its socket: a ring of cold light round its top, breathing (hidden while a gear sits on it).
    const sockGeo = this.track(new THREE.TorusGeometry(0.3, 0.05, 5, 18).rotateX(Math.PI / 2));
    const sockMat = this.track(new THREE.MeshBasicMaterial({ color: '#cfe0f2', transparent: true, opacity: 0.85, depthWrite: false }));
    this.sockMat = sockMat;
    for (const p of this.axleAt) {
      const m = new THREE.Mesh(sockGeo, sockMat);
      m.position.set(p.x, p.y + 0.6, p.z);
      m.renderOrder = 6;
      this.group.add(m);
      this.sockets.push(m);
    }
    // Round the drum's axle, on the floor, the way it asks to turn (pale; white when the train is right).
    const askGeo = this.track(new THREE.TorusGeometry(1.35, 0.07, 4, 28, Math.PI * 1.5).rotateX(-Math.PI / 2));
    const askMat = this.track(new THREE.MeshBasicMaterial({ color: '#e8eef8', transparent: true, opacity: 0.4, depthWrite: false }));
    const ask = new THREE.Mesh(askGeo, askMat);
    const askTip = new THREE.Mesh(this.track(new THREE.ConeGeometry(0.22, 0.46, 4).rotateZ(-Math.PI / 2)), askMat);
    askTip.position.set(Math.cos(Math.PI * 1.5) * 1.35, 0, -Math.sin(Math.PI * 1.5) * 1.35);
    askTip.rotation.y = Math.PI * 1.5 + Math.PI / 2;
    ask.add(askTip);
    const d1 = this.axleAt[1] as THREE.Vector3;
    ask.position.set(d1.x, d1.y + 0.05, d1.z);
    // (As built it turns counter-clockwise from above: the drum asks the other way from the wheel, ↺.)
    ask.renderOrder = 5;
    this.group.add(ask);
    this.askMat = askMat;
    // The forge's light on the floor: braziers by the wall, their glow pooled on the flags.
    for (const [q, r] of [[-3, -4], [4, -5], [-5, 3], [3, 4]] as const) {
      const p = this.hex(q, r);
      kit.add(new THREE.CylinderGeometry(0.34, 0.18, 0.42, 8), IRON, place(p.x, p.y + 0.55, p.z));
      kit.add(new THREE.CylinderGeometry(0.05, 0.05, 0.4, 5), IRON, place(p.x, p.y + 0.2, p.z));
      this.light(new THREE.Vector3(p.x, p.y + 0.85, p.z), RED_SOFT, 1.9, 0.9, 1);
      this.light(new THREE.Vector3(p.x, p.y + 0.15, p.z), RED_SOFT, 6.5, 0.22, 0.3);
    }
    // The arrows over the axles: a red three-quarter ring with its head, laid flat over the gear (its way by the sign
    // of its scale: as built it turns counter-clockwise seen from above).
    const END = Math.PI * 1.25;
    const arcGeo = this.track(new THREE.TorusGeometry(0.42, 0.06, 4, 16, END));
    arcGeo.rotateX(-Math.PI / 2);
    const headGeo = this.track(new THREE.ConeGeometry(0.15, 0.32, 4));
    headGeo.rotateZ(-Math.PI / 2);
    const arrowMat = this.track(new THREE.MeshBasicMaterial({ color: RED, transparent: true, opacity: 0.95, depthWrite: false }));
    for (let i = 0; i < L.axles.length; i++) {
      const m = new THREE.Mesh(arcGeo, arrowMat);
      const tip = new THREE.Mesh(headGeo, arrowMat);
      tip.position.set(Math.cos(END) * 0.42, 0, -Math.sin(END) * 0.42);
      tip.rotation.y = END + Math.PI / 2;
      m.add(tip);
      m.visible = false;
      m.renderOrder = 6;
      this.group.add(m);
      this.arrows.push(m);
    }
    box.dispose();
    cyl.dispose();
  }

  /** Where a lying piece rests (world point). */
  private lieAt(k: number): THREE.Vector3 {
    const L = this.Gl as GearLayout;
    const h = L.lie[k] as { q: number; r: number };
    return this.hex(h.q, h.r, 0.13);
  }

  /* ---------- the scenes ---------- */

  private buildGate(kit: Kit): void {
    const box = new THREE.BoxGeometry(1, 1, 1);
    const cyl = new THREE.CylinderGeometry(0.5, 0.56, 1, 10);
    // The road up from the south, its flags worn; the gate at its head between two towers, the wall either side.
    for (let r = -2; r <= 8; r++)
      for (let k = -2; k <= 2; k++) {
        const p = this.hex(0, r);
        const j = Math.sin(r * 12.9 + k * 7.7) * 0.5 + 0.5;
        kit.add(box, ['#3a4048', '#434a53', '#33393f'][Math.floor(j * 2.99)] as string, place(p.x + k * 0.34 + (r % 2) * 0.17, p.y + 0.015, p.z + (j - 0.5) * 0.1, (j - 0.5) * 0.2, new THREE.Vector3(0.3, 0.03, 0.4)));
      }
    const gz = -3.2 * S * 0.866;
    for (const k of [-1, 1]) {
      kit.add(cyl, '#5a616b', place(k * 2.0, 2.4, gz, 0, new THREE.Vector3(2.2, 4.8, 2.2)));
      for (let i = 0; i < 6; i++) {
        const a = (i / 6) * Math.PI * 2;
        kit.add(box, '#646b75', place(k * 2.0 + Math.cos(a) * 0.95, 5.05, gz + Math.sin(a) * 0.95, -a, new THREE.Vector3(0.36, 0.5, 0.3)));
      }
      kit.add(box, '#565d67', place(k * 6.0, 1.7, gz - 0.2, 0, new THREE.Vector3(6.0, 3.4, 1.2)));
      for (let i = 0; i < 8; i++) kit.add(box, '#646b75', place(k * (3.4 + i * 0.75), 3.6, gz - 0.2, 0, new THREE.Vector3(0.38, 0.45, 1.2)));
      this.light(new THREE.Vector3(k * 1.2, 2.2, gz + 1.25), WARM, 1.6, 0.85, 1);
      kit.add(box, IRON, place(k * 1.2, 1.95, gz + 1.2, 0, new THREE.Vector3(0.08, 0.5, 0.08)));
    }
    kit.add(box, '#565d67', place(0, 3.4, gz, 0, new THREE.Vector3(2.2, 1.2, 1.6)));
    kit.add(box, '#0d0f12', place(0, 1.3, gz + 0.3, 0, new THREE.Vector3(1.8, 2.6, 0.4)));
    // The portcullis up, its teeth showing under the arch.
    for (let i = 0; i < 6; i++) kit.add(box, IRON, place(-0.75 + i * 0.3, 2.65, gz + 0.62, 0, new THREE.Vector3(0.05, 0.55, 0.05)));
    // The keep's dark shape beyond, a lit window.
    kit.add(box, '#2b3038', place(0, 5.5, gz - 9, 0, new THREE.Vector3(5, 11, 4)));
    this.light(new THREE.Vector3(0.6, 7.2, gz - 6.9), WARM, 0.9, 0.6, 1);
    box.dispose();
    cyl.dispose();
  }

  private buildYard(kit: Kit): void {
    const box = new THREE.BoxGeometry(1, 1, 1);
    // The yard's curtain wall behind, its walk, crenels; barrels and crates along it.
    const wz = -4.6 * S * 0.866;
    kit.add(box, '#4b525b', place(0, 1.6, wz, 0, new THREE.Vector3(16, 3.2, 1.2)));
    for (let i = 0; i < 16; i++) kit.add(box, '#565d66', place(-7.5 + i, 3.45, wz + 0.2, 0, new THREE.Vector3(0.45, 0.5, 0.8)));
    for (let i = 0; i < 5; i++) {
      const p = this.hex(-4 + i * 2, -4);
      kit.add(new THREE.CylinderGeometry(0.26, 0.24, 0.6, 9), WOOD[i % 3] as string, place(p.x, p.y + 0.3, p.z + 0.6, i));
      if (i % 2) kit.add(box, WOOD[(i + 1) % 3] as string, place(p.x + 0.6, p.y + 0.25, p.z + 0.5, i * 0.4, new THREE.Vector3(0.5, 0.5, 0.5)));
    }
    // An anvil on its block, a brazier of coals, a rack of tongs.
    const a = this.hex(1, -1);
    kit.add(box, WOOD[0] as string, place(a.x, a.y + 0.22, a.z, 0.3, new THREE.Vector3(0.5, 0.44, 0.5)));
    kit.add(box, IRON, place(a.x, a.y + 0.52, a.z, 0.3, new THREE.Vector3(0.62, 0.18, 0.26)));
    const b = this.hex(-2, 1);
    kit.add(new THREE.CylinderGeometry(0.36, 0.2, 0.4, 8), IRON, place(b.x, b.y + 0.55, b.z));
    kit.add(new THREE.CylinderGeometry(0.04, 0.04, 0.6, 5), IRON, place(b.x, b.y + 0.3, b.z));
    this.light(new THREE.Vector3(b.x, b.y + 0.85, b.z), RED_SOFT, 2.0, 0.8, 1);
    this.light(new THREE.Vector3(b.x, b.y + 0.8, b.z), WARM, 0.8, 0.9, 1);
    this.lantern(kit, this.hex(2, 0), 1.6);
    box.dispose();
  }

  /** The scenes inside the rock: the workshop's top room, Nadd's forge, Tormo's cavern (a clearing of six). */
  private buildRoom(kit: Kit, place0: string): void {
    const R = this.map.radius;
    const rim: { q: number; r: number }[] = [];
    const open = new Set<string>();
    for (let r = -R; r <= R; r++)
      for (let q = -R; q <= R; q++) {
        const d = Math.max(Math.abs(q), Math.abs(r), Math.abs(q + r));
        if (d > R) continue;
        if (d <= 5) open.add(`${q},${r}`);
        else rim.push({ q, r });
      }
    const cave = place0 === 'tormoHall';
    if (cave) this.rockWalls(kit, rim, open, 3.4, 5.6, ['#2c3139', '#262a31', '#33383f', '#1f2328']);
    else this.workedWalls(kit, rim, open, 2.6);
    const box = new THREE.BoxGeometry(1, 1, 1);
    const cyl = new THREE.CylinderGeometry(0.5, 0.5, 1, 12);
    if (place0 === 'shopTop') {
      // The shaft in the floor with chains going down into the dark, a hoist frame over it.
      const s0 = this.hex(1, -3);
      kit.add(cyl, '#020203', place(s0.x, s0.y + 0.02, s0.z, 0, new THREE.Vector3(2.2, 0.04, 2.2)));
      kit.add(cyl, '#3c4148', place(s0.x, s0.y + 0.01, s0.z, 0, new THREE.Vector3(2.5, 0.02, 2.5)));
      for (const [dx, dz] of [[-1, 0], [1, 0]] as const) kit.add(box, WOOD[0] as string, place(s0.x + dx * 1.1, s0.y + 1.6, s0.z + dz, 0, new THREE.Vector3(0.14, 3.2, 0.14)));
      kit.add(box, WOOD[2] as string, place(s0.x, s0.y + 3.2, s0.z, 0, new THREE.Vector3(2.5, 0.16, 0.18)));
      for (const dx of [-0.4, 0.3]) kit.add(box, '#3a3f46', place(s0.x + dx, s0.y + 1.6, s0.z, 0, new THREE.Vector3(0.04, 3.2, 0.04)));
      // Nadd's bench: plans spread on it, a lamp, tools.
      const b = this.hex(-2, -1);
      kit.add(box, WOOD[1] as string, place(b.x, b.y + 0.45, b.z, 0.2, new THREE.Vector3(1.8, 0.1, 0.8)));
      for (const [dx, dz] of [[-0.8, -0.3], [0.8, -0.3], [-0.8, 0.3], [0.8, 0.3]] as const) kit.add(box, WOOD[0] as string, place(b.x + dx, b.y + 0.22, b.z + dz, 0.2, new THREE.Vector3(0.08, 0.44, 0.08)));
      for (let i = 0; i < 3; i++) kit.add(box, '#cfd3d8', place(b.x - 0.4 + i * 0.4, b.y + 0.51, b.z + (i - 1) * 0.08, 0.2 + i * 0.25, new THREE.Vector3(0.5, 0.01, 0.36)));
      this.lantern(kit, b, 0.85, WARM);
      // Pulleys on the walls, beams.
      for (const [q, r] of [[-4, 0], [4, -3], [2, 3]] as const) {
        const p = this.hex(q, r);
        const pg = gear(this.group, 0.36, 12, metal('#4a4e55', { roughness: 0.5 }), 0.07);
        pg.position.set(p.x, p.y + 2.0, p.z);
        pg.lookAt(0, p.y + 2, 0);
      }
      this.lantern(kit, this.hex(3, 1), 1.6);
    } else if (place0 === 'naddForge') {
      // The forge: a big hearth against the wall, its hood and chimney, an anvil, the bellows; the soup pot by the fire.
      const f = this.hex(0, -4);
      kit.add(box, '#4a4f57', place(f.x, f.y + 0.55, f.z, 0, new THREE.Vector3(2.2, 1.1, 1.2)));
      kit.add(box, '#16181c', place(f.x, f.y + 0.82, f.z + 0.42, 0, new THREE.Vector3(1.5, 0.4, 0.4)));
      kit.add(box, '#33373d', place(f.x, f.y + 2.0, f.z - 0.1, 0, new THREE.Vector3(1.9, 1.6, 0.9), -0.15, 0));
      this.light(new THREE.Vector3(f.x, f.y + 0.9, f.z + 0.5), RED, 2.6, 0.75, 0.5);
      this.light(new THREE.Vector3(f.x, f.y + 0.85, f.z + 0.5), WARM, 1.2, 0.8, 0.8);
      const an = this.hex(2, -2);
      kit.add(box, WOOD[0] as string, place(an.x, an.y + 0.22, an.z, 0.4, new THREE.Vector3(0.5, 0.44, 0.5)));
      kit.add(box, IRON, place(an.x, an.y + 0.52, an.z, 0.4, new THREE.Vector3(0.66, 0.18, 0.26)));
      const pot = this.hex(0, 0);
      kit.add(new THREE.CylinderGeometry(0.24, 0.2, 0.3, 10), IRON, place(pot.x + 0.15, pot.y + 0.4, pot.z, 0));
      for (const k of [-1, 1]) kit.add(box, IRON, place(pot.x + k * 0.5, pot.y + 0.4, pot.z, 0, new THREE.Vector3(0.04, 0.8, 0.04)));
      kit.add(box, IRON, place(pot.x, pot.y + 0.8, pot.z, 0, new THREE.Vector3(1.04, 0.04, 0.04)));
      // Old golem parts on the walls: an arm, a hand, gears.
      for (const [q, r] of [[-4, 1], [4, -1]] as const) {
        const p = this.hex(q, r);
        kit.add(box, '#3a3f46', place(p.x, p.y + 1.4, p.z, 0.6, new THREE.Vector3(0.28, 1.2, 0.28)));
        const pg = gear(this.group, 0.42, 14, metal('#5a4e3e', { roughness: 0.55 }), 0.07);
        pg.position.set(p.x, p.y + 2.3, p.z);
        pg.lookAt(0, p.y + 2.3, 0);
      }
    } else if (place0 === 'tormoHall') {
      // Tormo's cavern: one lamp hanging over where Tormo sits (the scene's figure), the lift's cage come down.
      const t = this.hex(0, -3);
      this.lantern(kit, new THREE.Vector3(t.x + 1.4, t.y + 2.6, t.z + 1.2), 1.2, WARM);
      const cg = this.hex(3, 1);
      kit.add(box, WOOD[1] as string, place(cg.x, cg.y + 0.06, cg.z, 0, new THREE.Vector3(1.4, 0.1, 1.4)));
      for (const [dx, dz] of [[-0.66, -0.66], [0.66, -0.66], [-0.66, 0.66], [0.66, 0.66]] as const) kit.add(box, WOOD[0] as string, place(cg.x + dx, cg.y + 0.7, cg.z + dz, 0, new THREE.Vector3(0.07, 1.3, 0.07)));
      kit.add(box, '#3a3f46', place(cg.x, cg.y + 3.2, cg.z, 0, new THREE.Vector3(0.04, 4, 0.04)));
      // Plans of Tormo pinned on the rock, crates of rivets, a scaffold against him.
      for (let i = 0; i < 4; i++) kit.add(box, WOOD[i % 3] as string, place(t.x - 2.0, t.y + 0.5 + i * 0.9, t.z + 0.6, 0, new THREE.Vector3(0.1, 0.1, 1.6)));
      for (const dx of [-2.0, -2.0]) kit.add(box, WOOD[0] as string, place(t.x + dx, t.y + 1.8, t.z + 1.4, 0, new THREE.Vector3(0.1, 3.6, 0.1)));
      for (let i = 0; i < 3; i++) kit.add(box, '#4a4038', place(t.x + 2.2 + i * 0.3, t.y + 0.25, t.z + 2.2 - i * 0.6, i * 0.5, new THREE.Vector3(0.5, 0.5, 0.5)));
    }
    box.dispose();
    cyl.dispose();
  }

  /**
   * The workshop broken into (the artificer's chapter 4 close, `SendaCine.broken`): Tormo sitting against the north
   * wall, headless, his chest open and the cavity empty (the heart taken); the door on the ledge's side (east) down,
   * split in planks on the floor, its frame cracked. Once: a second call does nothing.
   */
  breakIn(): void {
    if (this.broken) return;
    this.broken = true;
    const iron = this.track(new THREE.MeshStandardMaterial({ color: '#4a5058', roughness: 0.55, metalness: 0.55, flatShading: true }));
    const ironL = this.track(new THREE.MeshStandardMaterial({ color: '#6a717b', roughness: 0.45, metalness: 0.6, flatShading: true }));
    const stoneM = this.track(new THREE.MeshStandardMaterial({ color: '#7c848f', roughness: 0.9, flatShading: true }));
    const hollow = this.track(new THREE.MeshStandardMaterial({ color: '#08090b', roughness: 1 }));
    const wood = this.track(new THREE.MeshStandardMaterial({ color: '#8c6a4c', roughness: 0.9, flatShading: true }));
    const boxG = this.track(new THREE.BoxGeometry(1, 1, 1));
    const add = (m: THREE.Material, x: number, y: number, z: number, sx: number, sy: number, sz: number, rx = 0, ry = 0, rz = 0): THREE.Mesh => {
      const mesh = new THREE.Mesh(boxG, m);
      mesh.position.set(x, y, z);
      mesh.scale.set(sx, sy, sz);
      mesh.rotation.set(rx, ry, rz);
      mesh.castShadow = true;
      this.group.add(mesh);
      return mesh;
    };
    // Tormo, sitting against the north wall: legs out, the trunk upright, the shoulders' stones clamped on.
    const t = this.hex(-1, -3);
    for (const k of [-1, 1]) {
      add(iron, t.x + k * 0.55, t.y + 0.35, t.z + 1.0, 0.6, 0.6, 1.9);
      add(ironL, t.x + k * 0.55, t.y + 0.4, t.z + 2.0, 0.7, 0.8, 0.4);
      add(stoneM, t.x + k * 1.35, t.y + 2.6, t.z, 0.9, 0.8, 0.9);
      add(ironL, t.x + k * 1.35, t.y + 2.6, t.z + 0.48, 0.95, 0.12, 0.06);
      add(iron, t.x + k * 1.45, t.y + 1.6, t.z + 0.3, 0.45, 1.6, 0.45, 0.3, 0, k * 0.15);
    }
    add(iron, t.x, t.y + 1.6, t.z - 0.1, 2.0, 2.4, 1.1);
    // The chest open: its two plates swung out on their hinges, the cavity dark and empty.
    add(hollow, t.x, t.y + 1.75, t.z + 0.46, 1.0, 1.1, 0.05);
    for (const k of [-1, 1]) add(ironL, t.x + k * 0.95, t.y + 1.75, t.z + 0.85, 0.06, 1.15, 0.6, 0, k * 0.9, 0);
    add(ironL, t.x, t.y + 2.9, t.z - 0.1, 1.2, 0.2, 0.8);
    // The door on the ledge's side: down on the floor in planks, its frame cracked, the night outside.
    const d = this.hex(4, -1);
    for (let i = 0; i < 4; i++) add(wood, d.x - 0.6 - i * 0.35, d.y + 0.05, d.z + (i - 1.5) * 0.3, 0.3, 0.06, 1.3 - (i % 2) * 0.4, 0, 0.4 + i * 0.5, 0);
    add(wood, d.x - 0.2, d.y + 0.55, d.z + 0.7, 0.12, 1.1, 0.5, 0.6, 0, 0.3);
    add(iron, d.x - 0.9, d.y + 0.04, d.z - 0.6, 0.4, 0.04, 0.1, 0, 0.8, 0);
    this.light(new THREE.Vector3(d.x, d.y + 1.0, d.z), '#9fb4d8', 2.2, 0.35, 0);
  }

  /* ---------- the golems' marks ---------- */

  private buildGolemMarks(): void {
    const cone = this.track(fanGeo(0.9));
    const cm = this.track(new THREE.MeshBasicMaterial({ color: '#ffffff', vertexColors: true, transparent: true, opacity: 1, depthWrite: false, side: THREE.DoubleSide, blending: THREE.AdditiveBlending, polygonOffset: true, polygonOffsetFactor: -4 }));
    const cones = new THREE.InstancedMesh(cone, cm, 16);
    cones.instanceColor = new THREE.InstancedBufferAttribute(new Float32Array(16 * 3), 3);
    cones.count = 0;
    cones.renderOrder = 4;
    cones.frustumCulled = false;
    cones.name = 'taller-cones';
    this.cones = cones;
    this.group.add(cones);
    const arc = this.track(fanGeo(1, 12, 0.85));
    const am = this.track(new THREE.MeshBasicMaterial({ color: '#ffffff', vertexColors: true, transparent: true, opacity: 0.6, depthWrite: false, side: THREE.DoubleSide, polygonOffset: true, polygonOffsetFactor: -5 }));
    const arcs = new THREE.InstancedMesh(arc, am, 24);
    arcs.instanceColor = new THREE.InstancedBufferAttribute(new Float32Array(24 * 3), 3);
    arcs.count = 0;
    arcs.renderOrder = 5;
    arcs.frustumCulled = false;
    arcs.name = 'taller-arcs';
    this.arcs = arcs;
    this.group.add(arcs);
  }

  /* ---------- the board, repainted ---------- */

  apply(terrain: TerrainBuild, hour: Hour = 'day'): void {
    this.terrain = terrain;
    const c = new THREE.Color();
    const inside = this.inside;
    const col = terrain.mesh.geometry.getAttribute('color') as THREE.BufferAttribute | undefined;
    const pos = terrain.mesh.geometry.getAttribute('position') as THREE.BufferAttribute | undefined;
    // Inside the rock: one floor of worn flags (seams closed), and what isn't floor black.
    const rock = new Set<string>();
    for (const h of this.map.rocks ?? []) rock.add(`${h.q},${h.r}`);
    if (inside) smoothTiles(terrain.mesh.geometry, terrain.heights, this.map.radius, terrain.ground);
    if (col && pos) {
      terrain.mesh.updateMatrixWorld(true);
      const v = new THREE.Vector3();
      for (let i = 0; i < col.count; i++) {
        v.fromBufferAttribute(pos, i).applyMatrix4(terrain.mesh.matrixWorld);
        const lum = col.getX(i) * 0.3 + col.getY(i) * 0.59 + col.getZ(i) * 0.11;
        const k = 0.5 + 0.5 * Math.sin(v.x * 1.7 + v.z * 0.9) * Math.cos(v.z * 1.3 - v.x * 0.5);
        // The goat path worn pale by feet against the dark of the cliff and the sea.
        if (this.place === 'cornice') c.set(['#575d67', '#60666f', '#50565f'][Math.floor(k * 2.99)] as string).multiplyScalar(0.85 + lum * 0.35);
        else if (this.place === 'bastionGate') c.set(k > 0.6 ? '#2e3a33' : '#28322d').multiplyScalar(0.8 + lum * 0.5);
        else c.set(FLAG[Math.floor(k * 2.99)] as string).multiplyScalar((inside ? 0.86 : 0.8) + lum * 0.45);
        if (inside) {
          const hq = Math.round(v.x / S - v.z / (S * 0.866) / 2);
          const hr = Math.round(v.z / (S * 0.866));
          const d = Math.max(Math.abs(hq), Math.abs(hr), Math.abs(hq + hr));
          // (The lift's hall: dark slate past its walls, not black; black there framed the board in a void.)
          if (rock.has(`${hq},${hr}`) || d >= this.map.radius - 1 || (this.place !== 'workshop' && this.place !== 'gearHall' && d > 5)) c.set(this.place === 'gearHall' ? '#22262c' : '#050607');
        }
        col.setXYZ(i, c.r, c.g, c.b);
      }
      col.needsUpdate = true;
    }
    terrain.decor.traverse((o) => {
      if (o.name === 'roads') o.visible = false;
      // Inside the rock nothing of the world beyond the board; on the cliff, no woods.
      if (o.name === 'outskirts-ground') {
        if (inside) o.visible = false;
        else {
          const g = (o as THREE.Mesh).geometry.getAttribute('color') as THREE.BufferAttribute | undefined;
          if (g) {
            for (let i = 0; i < g.count; i++) {
              const lum = g.getX(i) * 0.3 + g.getY(i) * 0.59 + g.getZ(i) * 0.11;
              c.set(this.place === 'bastionGate' ? '#26302b' : SLATE[3] as string).multiplyScalar(0.8 + lum * 0.4);
              g.setXYZ(i, c.r, c.g, c.b);
            }
            g.needsUpdate = true;
          }
        }
      }
      if ((o as THREE.InstancedMesh).isInstancedMesh) o.visible = false;
    });
    if (this.place === 'cornice') {
      // One worn shelf of rock, not hex tiles: the seams closed.
      smoothTiles(terrain.mesh.geometry, terrain.heights, this.map.radius, terrain.ground);
      this.carve(terrain);
    }
    if (terrain.fog) terrain.fog.group.visible = !inside;
    if (!inside) {
      this.dome = new SkyDome();
      this.group.add(this.dome.mesh);
    }
    this.paint(hour);
  }

  paint(hour: Hour): void {
    const look = HOURS[this.inside ? 'inside' : hour];
    const scene = this.ctx.scene;
    scene.background = new THREE.Color(look.bg);
    if (scene.fog instanceof THREE.Fog) {
      scene.fog.color.set(look.fog[0]);
      scene.fog.near = look.fog[1];
      scene.fog.far = look.fog[2];
    }
    const { hemi, key, rim } = this.ctx;
    if (hemi) {
      hemi.color.set(look.hemi[0]);
      hemi.groundColor.set(look.hemi[1]);
      hemi.intensity = look.hemi[2];
    }
    if (key) {
      key.color.set(look.key[0]);
      key.intensity = look.key[1];
    }
    if (rim) {
      rim.color.set(look.rim[0]);
      rim.intensity = look.rim[1];
    }
    this.dome?.set(look.sky[0], look.sky[1], look.sky[2]);
    if (this.dome) this.dome.u.uSunDir.value.set(0.9, hour === 'dusk' ? 0.08 : 0.4, 0.15);
    if (this.sea) (this.sea.uniforms.dusk as THREE.IUniform<number>).value = hour === 'night' ? 1 : hour === 'dusk' ? 0.6 : 0;
    this.terrain?.fog?.tint({ low: look.fog[0], mid: look.fog[0], far: look.bg, alpha: 1 });
  }

  /* ---------- each tick and frame ---------- */

  tick(s: BattleState): void {
    this.tickNow = s.tick;
    for (const e of s._events ?? []) {
      if (e.t !== 'senda') continue;
      if (e.kind === 'stone') {
        const p = this.ctx.at(e.x, e.y, 0.1);
        this.ctx.shake(0.25);
        this.ctx.vfx.burst(p, { n: 14, color: '#9aa3ad', speed: 2.2, up: 1.2, size: 0.22, life: 10, smoke: true });
        this.ctx.vfx.ring(p, 0.2, 1.1, '#c9ced6', 6, 0.8);
      } else if (e.kind === 'stoneWarn') {
        // Pebbles and dust trickling down where it will fall.
        const p = this.ctx.at(e.x, e.y, 2.6);
        this.ctx.vfx.burst(p, { n: 6, color: '#8d959e', speed: 0.4, up: -0.6, size: 0.12, life: 14, gravity: 1 });
      } else if (e.kind === 'point') {
        this.pointer = { from: 0, nest: e.n ?? 0, until: s.tick + 30 };
      } else if (e.kind === 'sling') {
        this.ctx.vfx.burst(this.ctx.at(e.x, e.y, 0.6), { n: 5, color: '#a9b0b8', speed: 1.4, up: 0.6, size: 0.1, life: 6 });
      } else if (e.kind === 'golemWake') {
        this.ctx.flare(this.ctx.at(e.x, e.y, 1.3), '#ff4f3a', 2.2, 10);
      } else if (e.kind === 'golemHit') {
        const p = this.ctx.at(e.x, e.y, 0.1);
        this.ctx.shake(0.35);
        this.ctx.vfx.ring(p, 0.3, 1.6, '#ff8a6e', 6, 0.9);
        this.ctx.vfx.burst(p, { n: 10, color: '#7d848d', speed: 2, up: 0.8, size: 0.18, life: 8, smoke: true });
      } else if (e.kind === 'boiler') {
        const p = this.ctx.at(e.x, e.y, 1.4);
        this.ctx.vfx.burst(p, { n: 16, color: '#e6ebf0', speed: 1.2, up: 2.4, size: 0.4, life: 18, smoke: true });
        this.ctx.flare(p, '#ff8a6e', 1.6, 8);
      } else if (e.kind === 'bossOut') {
        const p = this.ctx.at(e.x, e.y, 1.6);
        this.ctx.shake(0.6);
        this.ctx.vfx.burst(p, { n: 30, color: '#e6ebf0', speed: 1.6, up: 3, size: 0.6, life: 26, smoke: true });
      } else if (e.kind === 'grille') {
        const i = Math.floor((e.n ?? 0) / 2);
        const g = this.grilles[i];
        if (g) g.want = (e.n ?? 0) % 2 ? 0 : 2.6;
        this.ctx.shake(0.18);
      } else if (e.kind === 'bolt') {
        const by = s.senda?.npcs.find((n) => n.id === e.npc);
        if (by) this.ctx.vfx.beam(this.ctx.at(by.x, by.y, 1.0), this.ctx.at(e.x, e.y, 0.9), '#e8eef8', 2, 0.03);
      } else if (e.kind === 'jam') {
        const p = this.ctx.at(e.x, e.y, 0.4);
        this.jamFlash = 1;
        this.ctx.shake(0.3);
        this.ctx.vfx.burst(p, { n: 22, color: '#ffb48a', speed: 3.2, up: 1.8, size: 0.1, life: 10, gravity: 1 });
        this.ctx.flare(p, '#ff4f3a', 1.8, 8);
      } else if (e.kind === 'right') {
        // The train right: a flash down every axle of it, the drum's ring lit.
        this.rightFlash = 1;
        this.ctx.flare(this.ctx.at(e.x, e.y, 1.0), '#ffffff', 3.4, 14);
        const w = s.senda?.gears;
        this.axleAt.forEach((p, i) => {
          if (w && (w.dir[i] ?? 0) !== 0) this.ctx.vfx.ring(p.clone().setY(p.y + 0.4), 0.3, 1.9, '#ffffff', 10, 0.9);
        });
      } else if (e.kind === 'stampWarn') {
        this.ctx.vfx.ring(this.ctx.at(e.x, e.y, 0.1), 0.4, 3.2, '#ff4f3a', 18, 0.7);
      } else if (e.kind === 'stamp') {
        this.ctx.shake(0.9);
        this.ctx.vfx.ring(this.ctx.at(e.x, e.y, 0.1), 0.5, 14, '#ff8a6e', 14, 0.8);
      } else if (e.kind === 'golemSpin' && e.n === 0) {
        this.ctx.shake(0.3);
        this.ctx.vfx.ring(this.ctx.at(e.x, e.y, 0.1), 0.6, 3.3, '#ff8a6e', 7, 0.9);
      } else if (e.kind === 'belt') {
        this.beltOn = true;
        this.ctx.shake(0.4);
        const L = this.Gl;
        if (L) this.ctx.vfx.burst(this.ctx.at(L.wheelAt.x, L.wheelAt.y, 1.2), { n: 26, color: '#cfe2f2', speed: 2.4, up: 2, size: 0.25, life: 16 });
      } else if (e.kind === 'knock') {
        this.ctx.shake(0.12);
      }
    }
  }

  draw(s: BattleState): void {
    this.tickNow = s.tick;
    this.lastState = s;
    this.drawStones(s);
    this.drawGolems(s);
    this.drawGears(s);
    // A grille drops into its stair when shut; up, it is gone into the rock over it.
    for (const g of this.grilles) {
      g.y += (g.want - g.y) * 0.35;
      g.mesh.position.y = this.ctx.ground(g.mesh.position.x, g.mesh.position.z) + g.y;
      g.mesh.visible = g.y < 2.3;
    }
  }

  private drawStones(s: BattleState): void {
    const sh = this.shadows;
    const w = s.senda?.cornice;
    const r = s.rules?.senda?.cornice;
    if (!sh || !w || !r) return;
    let n = 0;
    for (const st of w.stones) {
      if (n >= 15) break;
      const left = Math.max(0, st.t - s.tick);
      const k = 1 - left / Math.max(1, r.stone.warn);
      const p = this.ctx.at(st.x, st.y, 0.04);
      const rad = r.stone.r * K;
      // The dark disc grows as it comes; the red ring round the reach.
      this.m4.compose(p, this.q4.identity(), this.s3.set(rad * (0.35 + 0.65 * k), 1, rad * (0.35 + 0.65 * k)));
      sh.setMatrixAt(n, this.m4);
      sh.setColorAt(n, this.c3.set('#0a0b0e'));
      n++;
      this.m4.compose(p.setY(p.y + 0.005), this.q4, this.s3.set(rad, 1, rad));
      sh.setMatrixAt(n, this.m4);
      sh.setColorAt(n, this.c3.copy(RED).multiplyScalar(0.45 + 0.55 * k));
      n++;
    }
    sh.count = n;
    sh.instanceMatrix.needsUpdate = true;
    if (sh.instanceColor) sh.instanceColor.needsUpdate = true;
  }

  private drawGolems(s: BattleState): void {
    const gr = s.rules?.senda?.golem;
    const list = s.senda?.golem?.list;
    const cones = this.cones;
    const arcs = this.arcs;
    this.golemGlow.length = 0;
    if (!gr || !list || !cones || !arcs) return;
    let nc = 0;
    let na = 0;
    const up = new THREE.Vector3(0, 1, 0);
    for (const g of list) {
      if (g.mode === 3) continue;
      const e = s.enemies.find((x) => x.id === g.id);
      if (!e) continue;
      const at = this.ctx.pos(e.id) ?? this.ctx.at(e.x, e.y, 0);
      const ang = (e.facing / 1024) * Math.PI * 2;
      // The lamp's cone on the floor: asleep a warm sweep, awake red; none while out.
      if ((g.mode === 0 && gr.lamp.range > 0) || g.mode === 1) {
        const len = (g.mode === 0 ? gr.lamp.range : gr.lamp.range * 0.7) * K;
        const wide = Math.tan(((gr.lamp.arc / 1024) * Math.PI * 2) / 0.9);
        this.q4.setFromAxisAngle(up, -ang);
        this.m4.compose(this.v3.set(at.x, this.ctx.ground(at.x, at.z) + 0.05, at.z), this.q4, this.s3.set(len, 1, len * Math.min(1.2, wide * 0.9)));
        if (nc < 16) {
          cones.setMatrixAt(nc, this.m4);
          cones.setColorAt(nc, this.c3.copy(g.mode === 0 ? RED_SOFT : RED).multiplyScalar(g.mode === 0 ? 0.2 : 0.36));
          nc++;
        }
      }
      // Its arm up: the arc it will strike, red, pulsing faster as it nears.
      if (g.wind >= 0 && g.mode === 1 && na < 24) {
        const B = g.boss && gr.boss ? gr.boss : null;
        const reach = (B ? B.reach : gr.reach) * K;
        const half = (((B ? B.arc : gr.arc) / 1024) * Math.PI * 2);
        const left = Math.max(0, g.wind - s.tick) / Math.max(1, B ? B.warn : gr.warn);
        const aim = (g.aim / 1024) * Math.PI * 2;
        this.q4.setFromAxisAngle(up, -aim);
        this.m4.compose(this.v3.set(at.x, this.ctx.ground(at.x, at.z) + 0.06, at.z), this.q4, this.s3.set(reach, 1, reach * Math.sin(Math.min(1.5, half)) / Math.sin(1)));
        arcs.setMatrixAt(na, this.m4);
        const pulse = 0.5 + 0.5 * Math.sin(this.now * (8 + (1 - left) * 14));
        arcs.setColorAt(na, this.c3.copy(RED).multiplyScalar(0.45 + 0.55 * pulse));
        na++;
      }
      // Its arms out, spinning: the whole round of their reach, red, pulsing faster as it nears (four fans).
      if (g.spin >= 0 && na <= 20) {
        const left = Math.max(0, g.spin - s.tick) / Math.max(1, gr.spin.warn);
        const pulse = 0.5 + 0.5 * Math.sin(this.now * (8 + (1 - left) * 14));
        const rr = gr.spin.r * K;
        for (let k = 0; k < 4; k++) {
          this.q4.setFromAxisAngle(up, (k * Math.PI) / 2 + this.now * 2);
          this.m4.compose(this.v3.set(at.x, this.ctx.ground(at.x, at.z) + 0.07, at.z), this.q4, this.s3.set(rr, 1, rr * 0.92));
          arcs.setMatrixAt(na, this.m4);
          arcs.setColorAt(na, this.c3.copy(RED).multiplyScalar(0.3 + 0.4 * pulse));
          na++;
        }
      }
      // The lamp's glow at its chest; an open boiler glowing in its back, steam.
      const fx = Math.cos(ang);
      const fz = Math.sin(ang);
      const sc = g.boss ? 1.6 : 1.25;
      if (g.mode !== 2) this.golemGlow.push({ p: new THREE.Vector3(at.x + fx * 0.32 * sc, at.y + 1.0 * sc, at.z + fz * 0.32 * sc), c: g.mode === 0 ? WARM : RED, size: g.mode === 0 ? 0.9 : 1.3, a: g.mode === 0 ? 0.45 : 0.85 });
      const open = g.mode === 2 || (g.boss && g.open.some((o) => o > 0));
      if (open) {
        const pulse = 0.75 + 0.25 * Math.sin(this.now * 9);
        if (g.boss) {
          g.open.forEach((o, k) => {
            if (!(o > 0) && g.mode !== 2) return;
            const side = k ? 0.27 : -0.27;
            this.golemGlow.push({ p: new THREE.Vector3(at.x - fx * 0.75 * sc - fz * side * sc, at.y + 0.95 * sc, at.z - fz * 0.75 * sc + fx * side * sc), c: RED_SOFT, size: 1.1, a: 0.9 * pulse });
          });
        } else this.golemGlow.push({ p: new THREE.Vector3(at.x - fx * 0.7 * sc, at.y + 0.95 * sc, at.z - fz * 0.7 * sc), c: RED_SOFT, size: 1.2, a: 0.9 * pulse });
      }
    }
    cones.count = nc;
    cones.instanceMatrix.needsUpdate = true;
    if (cones.instanceColor) cones.instanceColor.needsUpdate = true;
    arcs.count = na;
    arcs.instanceMatrix.needsUpdate = true;
    if (arcs.instanceColor) arcs.instanceColor.needsUpdate = true;
  }

  /** Where each gear is now: on its axle (at its height), lying, or carried over the hero's head. */
  private drawGears(s: BattleState): void {
    const r = s.rules?.senda?.gears;
    const w = s.senda?.gears;
    if (!this.Gl) return;
    const L = this.Gl;
    const A = L.axles.length;
    const hero = s.heroes[0];
    this.gears.forEach((gv, k) => {
      const at = w ? (w.on[k] ?? A + k) : A + k;
      let want: THREE.Vector3;
      if (at < 0 && hero) {
        const hp = this.ctx.pos(hero.id) ?? this.ctx.at(hero.x, hero.y, 0);
        want = this.v3.set(hp.x, hp.y + 2.1, hp.z);
        gv.tilt += (1.2 - gv.tilt) * 0.3;
      } else if (at >= 0 && at < A) {
        const p = this.axleAt[at] as THREE.Vector3;
        want = this.v3.set(p.x, p.y + 0.3 + (this.axleLevel[at] ?? 0) * 0.07, p.z);
        gv.tilt += (0 - gv.tilt) * 0.3;
      } else {
        want = this.lieAt(at - A);
        if (w && at - A !== k) want = this.lieAt(at - A);
        gv.tilt += (0.05 - gv.tilt) * 0.3;
      }
      gv.pos.lerp(want, at < 0 ? 0.5 : 0.3);
      gv.g.position.copy(gv.pos);
      gv.g.rotation.set(gv.tilt, k * 0.7, 0);
      // Hidden past the belt if it was in the hands (dropped: the train is done).
      gv.g.visible = !(w && w.belt >= 0 && at < 0);
    });
    // The arrows over the turning axles: the way they turn, red; hidden on a still or jammed one.
    this.arrows.forEach((m, i) => {
      const dir = w && w.jam < 0 ? (w.dir[i] ?? 0) : 0;
      const has = !!w && gearAt(w, i) >= 0;
      m.visible = dir !== 0 && has;
      if (!m.visible) return;
      const p = this.axleAt[i] as THREE.Vector3;
      const teeth = w ? (r?.pieces[gearAt(w, i)] ?? 24) : 24;
      m.position.set(p.x, p.y + 0.62 + (this.axleLevel[i] ?? 0) * 0.07, p.z);
      const sc = gearR(teeth) / (0.42 * S) * 0.62;
      m.scale.set(sc * (dir > 0 ? -1 : 1), 1, sc);
    });
  }

  frame(now: number, camera?: THREE.Camera): void {
    const dt = this.now ? Math.min(0.1, now - this.now) : 0;
    this.now = now;
    this.dome?.frame(now, camera);
    if (this.sea) (this.sea.uniforms.time as THREE.IUniform<number>).value = now;
    for (const g of this.gulls) {
      const a = now * 0.35 + g.ph;
      g.m.position.set(g.c.x + Math.cos(a) * g.r, g.y + Math.sin(a * 1.7) * 0.3, g.c.z + Math.sin(a) * g.r);
      g.m.rotation.y = -a;
      g.m.scale.y = 1 + Math.sin(now * 6 + g.ph) * 0.6;
    }
    // The gears turn at their speed, their way; the wheel always; the drum with its axle.
    const s = this.lastState;
    const w = s?.senda?.gears;
    if (this.Gl && w) {
      this.gears.forEach((gv, k) => {
        const at = w.on[k] ?? -1;
        if (at < 0 || at >= this.Gl!.axles.length || w.jam >= 0) return;
        const dir = w.dir[at] ?? 0;
        const sp = w.speed[at] ?? 0;
        gv.angle -= dir * sp * dt * 0.045;
        gv.spin.rotation.y = gv.angle;
      });
      if (this.drum) this.drum.rotation.z += (w.jam < 0 ? (w.dir[1] ?? 0) * (w.speed[1] ?? 0) : 0) * dt * 0.05;
      if (this.cage) {
        const want = this.beltOn || w.belt >= 0 ? -3.2 : 0;
        this.cageY += (want - this.cageY) * Math.min(1, dt * 0.35);
        this.cage.position.y = this.cageY;
      }
    }
    if (this.wheel) this.wheel.rotation.x -= dt * 0.9;
    // Steam off the pipes' vents, one puff at a time (a handful of particles a second).
    if (this.vents.length && now > this.puff) {
      this.puff = now + 0.45;
      const v = this.vents[Math.floor((now * 7.3) % this.vents.length)] as THREE.Vector3;
      this.ctx.vfx.burst(v, { n: 3, color: '#d6dde6', speed: 0.3, up: 1.2, size: 0.45, life: 22, smoke: true });
    }
    this.jamFlash = Math.max(0, this.jamFlash - dt * 2);
    this.rightFlash = Math.max(0, this.rightFlash - dt * 0.6);
    if (w) {
      this.sockets.forEach((m, i) => (m.visible = gearAt(w, i) < 0 && w.belt < 0));
      if (this.sockMat) this.sockMat.opacity = 0.55 + 0.35 * Math.sin(now * 3.2);
      const right = w.solved >= 0 || w.belt >= 0;
      if (this.askMat) {
        this.askMat.opacity = right ? 0.95 : 0.38 + 0.12 * Math.sin(now * 2);
        this.askMat.color.set(right ? '#ffffff' : '#e8eef8');
      }
    }
    this.glow.begin();
    for (const l of this.lights) {
      const f = l.flick ? 0.86 + 0.14 * Math.sin(now * (5 + l.seed) + l.seed) * l.flick : 1;
      this.glow.add(l.p.x, l.p.y, l.p.z, l.c, l.size, l.a * f);
    }
    for (const g of this.golemGlow) this.glow.add(g.p.x, g.p.y, g.p.z, g.c, g.size, g.a);
    if (this.rightFlash > 0 && this.axleAt[1]) {
      const d = this.axleAt[1];
      this.glow.add(d.x, d.y + 0.6, d.z, COLD, 3 + 3 * this.rightFlash, this.rightFlash);
    }
    // Grillo's lamp on a slinger's nest: a red beam a moment.
    const C = this.C;
    if (C && this.pointer && s && s.tick < this.pointer.until) {
      const n = C.nestAt[this.pointer.nest];
      const gr = s.senda?.npcs.find((x) => x.id === 'grillo');
      if (n && gr) {
        const a = this.ctx.at(gr.x, gr.y, 0.85);
        const b = this.ctx.at(n.x, n.y, 0.6);
        for (let i = 0; i <= 8; i++) this.glow.add(a.x + (b.x - a.x) * (i / 8), a.y + (b.y - a.y) * (i / 8), a.z + (b.z - a.z) * (i / 8), RED, 0.55, 0.55 * (1 - i / 12));
        this.glow.add(b.x, b.y, b.z, RED, 1.6, 0.6);
      }
    }
    this.glow.end();
  }

  /** The state last drawn (the frame reads it between ticks). */
  private lastState: BattleState | null = null;

  dispose(): void {
    for (const m of this.mats) m.dispose();
    for (const g of this.geos) g.dispose();
    this.dome?.dispose();
    this.glow.mesh.geometry.dispose();
    this.glow.material.dispose();
    this.group.removeFromParent();
  }
}

/** A golem's view: its unit (the HUD's and the tests'). */
export type { GolemUnit };
